import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputOtp } from 'primeng/inputotp';
import { MessageModule } from 'primeng/message';
import { FirebaseAuthService } from '../../../services/firebase.auth.service';
import { FirebaseFirestoreService } from '../../../services/firebase.firestore.service';
import { NumericInputModeDirective } from '../../../directives/numeric-input-mode.directive';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
  selector: 'app-delete-account-reauth-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputOtp,
    MessageModule,
    NumericInputModeDirective
  ],
  templateUrl: './delete-account-reauth-modal.component.html',
  styleUrls: ['./delete-account-reauth-modal.component.scss']
})
export class DeleteAccountReauthModalComponent {
  firebaseAuthService = inject(FirebaseAuthService);
  firebaseFirestoreService = inject(FirebaseFirestoreService);
  dialogRef = inject(DynamicDialogRef);
  
  // Form controls
  otp = new FormControl('', [Validators.maxLength(6), Validators.minLength(6), Validators.pattern(`^[0-9]*$`)]);
  
  // State management
  isLoading = signal(false);
  errorMessage = signal('');
  successMessage = signal('');
  phoneNumber = signal('');
  verificationId = signal('');
  
  constructor() {
    // Get user's phone number from profile
    const userProfile = this.firebaseFirestoreService.getUserProfile();
    if (userProfile?.phoneNumber) {
      this.phoneNumber.set(userProfile.phoneNumber);
      this.sendVerificationCode();
    }
  }
  
  async sendVerificationCode() {
    try {
      this.isLoading.set(true);
      this.errorMessage.set('');
      
      // Send verification code to user's phone
      const verificationId = await this.firebaseAuthService.sendVerificationCodeForReauth(this.phoneNumber());
      this.verificationId.set(verificationId);
      this.successMessage.set('Verification code sent to your phone');
      
    } catch (error) {
      console.error('Failed to send verification code:', error);
      this.errorMessage.set('Failed to send verification code. Please try again.');
    } finally {
      this.isLoading.set(false);
    }
  }
  
  async verifyOTPAndDeleteAccount() {
    if (!this.otp.valid) {
      this.errorMessage.set('Please enter a valid 6-digit OTP');
      return;
    }
    
    try {
      this.isLoading.set(true);
      this.errorMessage.set('');
      
      const result = await this.firebaseAuthService.deleteAccountWithReauth(
        this.phoneNumber(),
        this.verificationId(),
        this.otp.getRawValue() as string
      );
      
      if (result.success) {
        this.successMessage.set('Account deleted successfully');
        // Close modal and navigate to login
        setTimeout(() => {
          this.closeModal();
        }, 2000);
      } else if (result.needsReauth) {
        this.errorMessage.set('Verification failed. Please try again.');
        this.otp.reset();
      } else {
        this.errorMessage.set(result.message);
      }
      
    } catch (error) {
      console.error('Account deletion failed:', error);
      this.errorMessage.set('Account deletion failed. Please try again.');
    } finally {
      this.isLoading.set(false);
    }
  }
  
  closeModal() {
    this.dialogRef.close();
  }
}
